import { useMemo, useRef, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type PasoManual } from "@/lib/api";
import { aPayloadPaso, datoVacio, formPaso, pasosPorSubcategoria, subcategoriasEmpezadas, textoCopia, textoSugeridos, validarPaso, type FormPaso } from "@/lib/manual";

type Dialogo = { tipo: "form"; itemId: number; paso: PasoManual | null } | { tipo: "borrar"; paso: PasoManual } | { tipo: "copiar" } | null;

/**
 * Manual de obra en Plata → Presupuestos (sin JSX; Mati, 2026-10-30): los pasos de cada subcategoría, tildarlos,
 * anotar el dato que piden, agregar, editar, borrar y mover, y cargar el borrador sugerido en las que no tienen. Escribe
 * sólo el admin. **Copiar de otro desarrollo** trae categorías, subcategorías y pasos de otro donde también es admin;
 * `alCambiar` vuelve a pedir el árbol. La UI está en `PasosSubcategoria.tsx`.
 */
export function usePasosManual(esAdmin: boolean, alCambiar: () => void) {
  const { fid, usuario } = useContextoApp();
  const otrosDesarrollos = usuario.membresias.filter((m) => m.rol === "admin" && m.fideicomiso_id !== fid);
  const [desde, setDesde] = useState<string | undefined>(undefined);
  const base = `/api/fideicomisos/${fid}/manual`;
  const recurso = useApiResource<PasoManual[]>(`${base}/pasos`);
  const pasos = useMemo(() => recurso.data ?? [], [recurso.data]);

  // Lo recién tildado o movido se ve al toque; al volver del servidor manda lo de él.
  const [pendientes, setPendientes] = useState<Record<number, Partial<PasoManual>>>({});
  const [ordenes, setOrdenes] = useState<Record<number, number[]>>({});
  const vistos = useMemo(() => pasos.map((p) => ({ ...p, ...pendientes[p.id] })), [pasos, pendientes]);
  const porItem = useMemo(() => {
    const m = pasosPorSubcategoria(vistos);
    for (const [item, ids] of Object.entries(ordenes)) {
      const actuales = m.get(Number(item));
      if (actuales) m.set(Number(item), ids.map((id) => actuales.find((p) => p.id === id)).filter((p): p is PasoManual => p !== undefined));
    }
    return m;
  }, [vistos, ordenes]);

  const [ordenando, setOrdenando] = useState<number | null>(null); // la subcategoría cuyos pasos se están moviendo
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormPaso>(formPaso());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const errores = validarPaso(form);

  const cola = useRef<Promise<unknown>>(Promise.resolve());
  const enCola = (pedido: () => Promise<unknown>, siFalla: () => void) => {
    setError(null);
    cola.current = cola.current.then(() =>
      pedido().then(recurso.recargar, (e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudo guardar");
        siFalla();
      }),
    );
  };

  const accionGuardar = useAsyncAction((d: Extract<Dialogo, { tipo: "form" }>, f: FormPaso) =>
    d.paso === null ? api.post<PasoManual>(`${base}/subcategorias/${d.itemId}/pasos`, aPayloadPaso(f)) : api.patch<PasoManual>(`${base}/pasos/${d.paso.id}`, aPayloadPaso(f)),
  );
  const accionBorrar = useAsyncAction((p: PasoManual) => api.delete<void>(`${base}/pasos/${p.id}`).then(() => true));
  const accionSugeridos = useAsyncAction(() => api.post<{ subcategorias: number; pasos: number }>(`${base}/sugeridos`, {}));
  const accionCopiar = useAsyncAction((origen: number) => api.post<{ categorias: number; subcategorias: number; pasos: number }>(`${base}/copiar`, { desde: origen }));

  const cerrar = () => setDialogo(null);
  const abrirForm = (itemId: number, paso: PasoManual | null) => {
    setForm(formPaso(paso ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", itemId, paso });
  };

  return {
    puedeEditar: esAdmin,
    cargando: recurso.loading && !recurso.data,
    errorCarga: recurso.data ? null : (recurso.error?.message ?? null),
    /** Subcategorías con algún paso hecho (en trámite aunque no tengan presupuestos). */
    empezadas: useMemo(() => subcategoriasEmpezadas(vistos), [vistos]),
    pasosDe: (itemId: number) => porItem.get(itemId) ?? [],
    hayPasos: pasos.length > 0,
    error,
    aviso,
    ordenando,
    alternarOrden: (itemId: number) => setOrdenando((o) => (o === itemId ? null : itemId)),
    tildar: (p: PasoManual, hecho: boolean) => {
      setPendientes((x) => ({ ...x, [p.id]: { hecho_en: hecho ? (p.hecho_en ?? new Date().toISOString().slice(0, 10)) : null } }));
      enCola(
        () => api.patch(`${base}/pasos/${p.id}`, { hecho }).finally(() => setPendientes(({ [p.id]: _, ...resto }) => resto)),
        () => undefined,
      );
    },
    mover: (itemId: number, ids: number[]) => {
      setOrdenes((o) => ({ ...o, [itemId]: ids }));
      enCola(
        () => api.put(`${base}/subcategorias/${itemId}/orden`, { ids }),
        () => setOrdenes(({ [itemId]: _, ...resto }) => resto),
      );
    },
    cargarSugeridos: async () => {
      setAviso(null);
      const r = await accionSugeridos.run();
      if (r) {
        setAviso(textoSugeridos(r));
        recurso.recargar();
      }
    },
    cargandoSugeridos: accionSugeridos.loading,
    puedeCopiar: esAdmin && otrosDesarrollos.length > 0,
    opcionesDesarrollo: otrosDesarrollos.map((m) => ({ valor: String(m.fideicomiso_id), etiqueta: m.fideicomiso_nombre })),
    desde,
    setDesde,
    abrirCopiar: () => {
      accionCopiar.limpiarError();
      setDesde(otrosDesarrollos.length === 1 ? String(otrosDesarrollos[0]!.fideicomiso_id) : undefined);
      setDialogo({ tipo: "copiar" });
    },
    copiar: async () => {
      if (!desde) return;
      setAviso(null);
      const r = await accionCopiar.run(Number(desde));
      if (r) {
        setAviso(textoCopia(r));
        setDialogo(null);
        recurso.recargar();
        alCambiar();
      }
    },
    copiando: accionCopiar.loading,
    errorCopiar: accionCopiar.error,
    errorSugeridos: accionSugeridos.error,
    // diálogos
    dialogo,
    cerrar,
    abrirNuevo: (itemId: number) => abrirForm(itemId, null),
    abrirEditar: (p: PasoManual) => abrirForm(p.item_id, p),
    abrirBorrar: (p: PasoManual) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", paso: p });
    },
    form,
    cambiar: <K extends keyof FormPaso>(campo: K, valor: FormPaso[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    cambiarDato: (i: number, campo: "nombre" | "quien" | "valor", valor: string) =>
      setForm((f) => ({ ...f, datos: f.datos.map((d, j) => (j === i ? { ...d, [campo]: valor } : d)) })),
    agregarDato: () => setForm((f) => ({ ...f, datos: [...f.datos, datoVacio()] })),
    quitarDato: (i: number) => setForm((f) => ({ ...f, datos: f.datos.filter((_, j) => j !== i) })),
    errores: mostrarErrores ? errores : {},
    guardar: async (e: FormEvent) => {
      e.preventDefault();
      setMostrarErrores(true);
      if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
      if ((await accionGuardar.run(dialogo, form)) !== undefined) {
        cerrar();
        recurso.recargar();
      }
    },
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar: async () => {
      if (dialogo?.tipo !== "borrar") return;
      if (await accionBorrar.run(dialogo.paso)) {
        cerrar();
        recurso.recargar();
      }
    },
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}

export type PasosManual = ReturnType<typeof usePasosManual>;
